import React from "react";

const machineLearning = () => {
  return (
    <div className="flex justify-center min-h-screen text-justify bg-gray-100">
            <div className="container mx-auto p-3 sm:px-6 lg:px-44">
        <h1 className="py-10 text-4xl font-bold text-center">
          Explorando Salários na NBA com Machine Learning e Visualização de
          Dados
        </h1>
        <div className="gap-6">
          <h2 className="text-3xl font-semibold">I. INTRODUÇÃO</h2>
          <br />
          <p className="text-xl">
            O Universo da National Basketball Association (NBA) transcende a
            competição nas quadras, revelando-se igualmente fascinante nos
            bastidores. Além do espetáculo esportivo, a NBA é notoriamente
            reconhecida por seus contratos milionários, que refletem não apenas
            a destreza atlética, mas também o valor intrínseco de cada jogador
            para suas equipes.
          </p>
          <br />
          <p className="text-xl">
            Neste contexto envolvente, propomos uma imersão no mundo dos
            salários na NBA, utilizando uma abordagem inovadora que mescla
            machine learning e visualização de dados. Este projeto visa
            desvendar os padrões subjacentes aos vultosos salários dos
            jogadores, proporcionando uma compreensão detalhada das variáveis
            que influenciam as compensações no universo do basquete
            profissional.
          </p>
          <br />
          <p className="text-xl">
            Ao longo deste artigo, seremos guiados por uma jornada que abrange
            desde a coleta e limpeza meticulosa de dados até a construção de
            modelos de machine learning capazes de antecipar os salários dos
            jogadores com base em métricas específicas. Além disso, exploraremos
            visualizações interativas que lançam luz sobre a distribuição
            salarial, relacionamentos estatísticos intrigantes e a dinâmica dos
            salários em diferentes times.
          </p>
          <br />
          <p className="text-xl">
            Esta incursão na interseção entre dados e esporte não apenas
            desvendará os segredos por trás dos contratos milionários, mas
            também ilustrará o poder transformador da análise de dados no
            contexto da NBA. Prepare-se para uma viagem cativante pelos
            corredores digitais onde as estatísticas e os algoritmos se
            encontram com os dribles e as enterradas, revelando facetas
            inexploradas do universo da NBA.
          </p>

          <br />
          <h2 className="text-3xl font-semibold">II. MÉTODOS</h2>
          <br />
          <div className="space-y-6">
            <div className="space-y-1">
              <p className="text-2xl font-bold">A. Coleta de Dados</p>
              <p className="text-xl">
                Para construir uma análise robusta dos salários na NBA, é
                imperativo começar com uma coleta abrangente e precisa de dados.
                Utilizamos dataset disponibilizado no Kaggle. Essa abordagem
                garantiu a obtenção de informações detalhadas sobre os salários
                dos jogadores, suas estatísticas de desempenho e outros fatores
                relevantes.
              </p>
              <p className="italic text-xl">
                <img src="/img11.webp" alt="Dataset" />
                Figura 1: Dataset
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">
                B. Limpeza e Preparação dos Dados
              </p>
              <p className="text-xl">
                A integridade dos resultados depende significativamente da
                qualidade dos dados. Implementamos técnicas avançadas de limpeza
                e preparação para lidar com inconsistências, dados ausentes e
                outliers. Isso envolveu a normalização de valores, tratamento de
                dados nulos e a criação de um conjunto de dados coeso e
                confiável para análise.
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">C. Seleção de Atributos</p>
              <p className="text-xl">
                Identificar os atributos mais relevantes é crucial para a
                eficácia dos modelos de machine learning. Exploramos uma
                variedade de características, incluindo estatísticas de
                desempenho em quadra, posição do jogador, tempo de experiência
                na liga e contribuição para o sucesso da equipe. A seleção
                cuidadosa desses atributos foi fundamental para modelar de
                maneira precisa a complexidade dos salários na NBA.
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">D. Modelagem Preditiva</p>
              <p className="text-xl">
                Empregamos algoritmos de machine learning, como regressão
                linear, para desenvolver modelos preditivos de salários. Esses
                modelos foram treinados com dados históricos, permitindo-nos
                antecipar com precisão os salários com base em diferentes
                conjuntos de variáveis.
              </p>
              <p className="italic text-xl">
                <img src="/img2.webp" alt="Dataset" />
                Figura 2: Regressão Linear
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">E. Avaliação e Ajustes</p>
              <p className="text-xl">
                A validação dos modelos é uma etapa crítica. Utilizamos métricas
                de desempenho, como erro médio absoluto e coeficiente de
                determinação (R²), para avaliar a eficácia de nossas previsões.
                Ajustes iterativos foram realizados para otimizar a precisão dos
                modelos, garantindo que capturassem nuances e tendências
                específicas do contexto da NBA.
              </p>
            </div>

            <p className="text-xl">
              Esta metodologia, integrando coleta detalhada de dados, limpeza
              criteriosa, seleção de atributos estratégica e modelagem preditiva
              avançada, constitui a espinha dorsal de nossa abordagem analítica.
              A próxima seção revelará as descobertas intrigantes resultantes
              desta incursão metodológica nos salários dos jogadores da NBA.
            </p>
          </div>

          <br />
          <h2 className="text-3xl font-semibold">III. RESULTADOS</h2>
          <br />
          <p className="text-xl">
            Os resultados desta análise profunda dos salários na National
            Basketball Association (NBA) oferecem uma visão esclarecedora sobre
            os fatores que moldam a compensação dos jogadores. Ao explorar
            múltiplos aspectos, desde estatísticas de desempenho até experiência
            na liga, pudemos identificar padrões e tendências significativas.
          </p>
          <br></br>
          <p className="text-xl">
            Em relação ao desempenho estatístico, descobrimos que estatísticas
            como pontos por jogo, rebotes e assistências desempenham um papel
            crucial na determinação dos salários dos jogadores. Jogadores com
            desempenho excepcional nessas categorias tendem a receber
            compensações mais elevadas, destacando a importância da contribuição
            individual para o sucesso da equipe.
          </p>
          <br></br>
          <p className="text-xl">
            A posição dos jogadores também emergiu como um fator determinante.
            Posições específicas, como armadores e alaspivôs, geralmente recebem
            salários mais altos em comparação com outras posições. Essa
            disparidade salarial pode ser atribuída às demandas específicas de
            cada posição e à escassez de talento em certas áreas.
          </p>
          <br></br>
          <p className="text-xl">
            A experiência na liga revelou ser um preditor consistente de
            salários. Jogadores mais experientes, com vários anos de
            participação na NBA, geralmente desfrutam de compensações mais
            substanciais. No entanto, a relação entre anos de experiência e
            salário não é linear, sugerindo que há nuances na forma como a
            experiência impacta a remuneração.
          </p>
          <br></br>
          <p className="text-xl">
            Além disso, ao considerar a contribuição para o sucesso da equipe,
            identificamos que jogadores cujo desempenho está fortemente
            correlacionado com a vitória da equipe tendem a receber salários
            mais altos. Isso destaca a valorização não apenas do desempenho
            individual, mas também da capacidade de impactar positivamente os
            resultados do time.
          </p>
          <br></br>

          <p className="text-xl">
            Em síntese, os resultados desta análise oferecem uma compreensão
            aprofundada dos fatores que influenciam os salários na NBA,
            contribuindo para o conhecimento sobre a dinâmica financeira do
            basquete profissional.
          </p>

          <br />
          <div className="space-y-6">
            <div className="space-y-1">
              <p className="text-2xl font-bold">
                1) Análise de Regressão para Estimativa de Salários
              </p>
              <p className="text-xl">
                Utilizando um modelo de regressão linear, exploramos a relação
                entre estatísticas de desempenho individual e salários. O modelo
                foi treinado com uma ampla gama de dados, incluindo arremessos
                de campo, arremessos de três pontos, assistências, entre outros.
                Com base nesses dados, conseguimos estimar salários com
                precisão, permitindo uma compreensão mais clara das variáveis
                que mais impactam a compensação dos jogadores.
              </p>
              <p className="italic text-xl">
                <img src="/img3.webp" alt="Dataset" />
                Figura 3: Estimador de Salário
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">
                2) Gráfico de Dispersão: Salário x Cestas por Jogo
              </p>
              <p className="text-xl">
                Visualizamos a relação entre o salário dos jogadores e seu
                desempenho em termos de cestas por jogo. O gráfico de dispersão
                revelou tendências interessantes, destacando como o rendimento
                cestas/salário varia entre diferentes jogadores. Essa análise
                proporciona uma visão intuitiva da eficiência relativa em termos
                de pontuação e remuneração.
              </p>
              <p className="italic text-xl">
                <img src="/img4.webp" alt="Dataset" />
                Figura 4: Salário x Cestas por Jogo
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">
                3) Perfil de Rebotes por Time
              </p>
              <p className="text-xl">
                Investigamos a soma das médias de rebotes por equipe,
                identificando padrões que evidenciam a importância dos rebotes
                no desempenho coletivo. O gráfico oferece uma compreensão visual
                da distribuição do desempenho de rebotes nas equipes da NBA.
              </p>
              <p className="italic text-xl">
                <img src="/img5.webp" alt="Dataset" />
                Figura 5: Rebotes por Time
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">
                4) Concentração de Salários para Somar 1 Bilhão
              </p>
              <p className="text-xl">
                Criamos um gráfico que destaca os jogadores cujos salários,
                quando somados, totalizam 1 bilhão de dólares. Essa análise
                proporciona uma visão única sobre a distribuição de salários e
                destaca a elite financeira dentro da liga.
              </p>
              <p className="italic text-xl">
                <img src="/img6.webp" alt="Dataset" />
                Figura 6: Concentração de Salários para Somar 1 Bilhão
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-2xl font-bold">
                5) Principais Estatísticas por Posição
              </p>
              <p className="text-xl">
                Apresentamos visualizações gráficas das principais estatísticas,
                como pontos, assistências e rebotes, segmentadas por posição.
                Essa análise destaca as características distintas de desempenho
                para diferentes posições na NBA.
              </p>
              <p className="italic text-xl">
                <img src="/img7.webp" alt="Dataset" />
                Figura 7: Principais Estatísticas por Posição
              </p>
              <p className="text-xl">
                Esses resultados combinados oferecem uma compreensão holística
                da relação entre desempenho e compensação na NBA, proporcionando
                insights valiosos para equipes, jogadores e entusiastas do
                basquete. Essa abordagem analítica contribui significativamente
                para a compreensão da complexidade envolvida na determinação dos
                salários dos jogadores na NBA.
              </p>
            </div>
          </div>

          <br />
          <h2 className="text-3xl font-semibold">IV. CONCLUSÃO</h2>
          <br />
          <p className="text-xl">
            Esta análise abrangente dos salários na National Basketball
            Association (NBA) proporciona uma visão valiosa sobre os mecanismos
            complexos que determinam a compensação dos jogadores. Ao examinar
            diversos fatores, desde desempenho estatístico até experiência na
            liga, pudemos identificar padrões que refletem a dinâmica única do
            mundo do basquete profissional.
          </p>
          <br></br>
          <p className="text-xl">
            É evidente que o desempenho individual, medido por estatísticas como
            pontos, rebotes e assistências, continua a ser um dos principais
            impulsionadores dos salários dos jogadores. A excepcional
            contribuição de um jogador nessas áreas fundamentais está
            diretamente correlacionada com uma remuneração mais elevada,
            destacando a importância do impacto individual no sucesso da equipe.
          </p>
          <br></br>
          <p className="text-xl">
            A disparidade salarial entre posições específicas também chama a
            atenção para as nuances do jogo. Jogadores em posições estratégicas,
            como armadores e alas-pivôs, recebem compensações superiores,
            refletindo as demandas específicas dessas funções e a escassez de
            talento nessas áreas.
          </p>
          <br></br>
          <p className="text-xl">
            A experiência na liga, embora desempenhe um papel significativo na
            determinação dos salários, revelou-se um fator complexo. Enquanto
            jogadores mais experientes geralmente desfrutam de compensações
            substanciais, a relação não é linear, indicando que outros fatores
            também desempenham um papel crucial na avaliação do valor de um
            jogador.
          </p>
          <br></br>
          <p className="text-xl">
            Além disso, observamos que o desempenho em rebotes emerge como um
            dos fatores mais significativos na determinação dos salários.
            Jogadores com maior número de rebotes tendem a receber compensações
            mais elevadas, sublinhando a valorização da contribuição na área de
            rebotes para o sucesso individual e coletivo.
          </p>
          <br></br>
          <p className="text-xl">
            Por fim, a contribuição para o sucesso da equipe, tanto em termos de
            desempenho quanto de impacto nos resultados, emergiu como um fator
            essencial. Jogadores que não apenas brilham individualmente, mas
            também impulsionam o êxito coletivo, são recompensados com salários
            mais altos, sublinhando a valorização da capacidade de influenciar
            positivamente o destino da equipe.
          </p>
          <br></br>
          <p className="text-xl">
            Esta análise não apenas fornece insights valiosos para os
            entusiastas do basquete, gestores de equipe e analistas esportivos,
            mas também destaca a complexidade e a diversidade de elementos que
            contribuem para a determinação dos salários na NBA. À medida que a
            liga evolui, a compreensão desses fatores torna-se crucial para as
            equipes maximizarem seus investimentos e para os jogadores navegarem
            no cenário competitivo com uma compreensão informada de seu próprio
            valor.
          </p>

          <br />
          <h2 className="text-3xl font-semibold">REFERÊNCIAS</h2>
          <br />
          <ul className="list-disc list-inside text-xl">
            <li>
              <a
                href="https://ivanildo-batista13.medium.com/regress%C3%A3o-linear-m%C3%BAltipla-em-python-eb4b6603a3"
                target="_blank"
              >
                Batista, I. (23 jul 2021). Regressão Linear Múltipla em python.
              </a>
            </li>
            <li>
              <a
                href="https://www.kaggle.com/code/emineyetm/multiple-linear-regression-in-python"
                target="_blank"
              >
                Bozku¸s, E. (4 dec 2022). Multiple linear regression in python.
              </a>
            </li>
            <li>
              <a href="https://diegomariano.com/d3-js/" target="_blank">
                Mariano, D. (15 aug 2020). D3.js.
              </a>
            </li>
            <li>
              <a
                href="https://doi.org/10.1007/s41060-018-0044-0"
                target="_blank"
              >
                Morgulev, E., Azar, O.H., Lidor, R.: Sports analytics and the
                big-data era.
              </a>
            </li>
            <li>
              <a
                href="https://datatofish.com/multiple-linear-regression-python/"
                target="_blank"
              >
                Data to Fish (30 jul 2022). Example of Multiple Linear
                Regression in Python.
              </a>
            </li>
            <li>
              <a href="https://d3js.org/getting-started" target="_blank">
                Getting started ([S.d.]).
              </a>
            </li>
            <li>
              <a
                href="https://flask.palletsprojects.com/en/3.0.x/"
                target="_blank"
              >
                Welcome to flask ([S.d.]).
              </a>
            </li>
            <li>
              <a
                href="https://www.kaggle.com/code/ahammedjaleel/startup-rest-api-with-flask"
                target="_blank"
              >
                Jaleel, A. (14 jan 2020). Startup: REST API with flask.
              </a>
            </li>
          </ul>
          <br></br>
          <h2 className="text-3xl font-semibold">AUTORES</h2>
          <br />
          <ul className="list-disc list-inside text-xl">
            <li>
              Dimas Ferreira             
                <a href="https://www.linkedin.com/in/dimas7/" className="ml-4 text-blue-500 hover:underline" target="_blank">
                    Linkedin
                  </a>              
            </li> 
            <li>
            Matheus Sass          
                <a href="https://www.linkedin.com/in/sassmatheus/" className="ml-4 text-blue-500 hover:underline" target="_blank">
                    Linkedin
                  </a>              
            </li> 
            <li>
            Husani Santos           
                <a href="linkedin.com/in/husanisantos" className="ml-4 text-blue-500 hover:underline" target="_blank">
                    Linkedin
                  </a>              
            </li> 
          </ul>
        </div>
      </div>
    </div>
  );
};
export default machineLearning;
